import { db } from "../../../../../firebaseConfig";
import { auth } from "../../../../../firebaseConfig";
import {
  doc,
  getDoc,
  deleteDoc,
  collection,
  addDoc,
  updateDoc,
  query,
  orderBy,
  limit,
  startAfter,
  getDocs,
  serverTimestamp,
  increment,
} from "firebase/firestore";
import { tokenizeWords } from "./utils.js";
import { getDisplayName } from "../../../../../utils/userDisplay.js";

/**
 * Save post to Firebase
 * @param {Object} post - Post data
 * @returns {Promise<Object>} - Saved post
 */
export const savePostToFirebase = async (post) => {
  try {
    if (!post.actionTag || !post.title) {
      throw new Error("Missing required post fields");
    }
    if (post.type !== 'shared_post' && !post.content) {
      throw new Error("Post content is required");
    }

    const validScales = ["global", "national", "state", "regional", "local"];
    if (!validScales.includes(post.scale)) {
      console.warn(`Invalid scale value: ${post.scale}. Defaulting to 'local'`);
      post.scale = "local";
    }

    const postsRef = collection(db, "posts");
    const currentUser = auth.currentUser;

    if (!currentUser) {
      throw new Error("User must be authenticated to create a post");
    }

    let resolvedUsername = post.username;
    let resolvedProfession = post.userProfession;
    let resolvedPhotoURL = post.userPhotoURL;

    try {
      const userRef = doc(db, "users", currentUser.uid);
      const userSnap = await getDoc(userRef);
      const profile = userSnap.exists() ? userSnap.data() : {};

      resolvedUsername = getDisplayName({
        profile: profile || null,
        authUser: currentUser || null,
        candidates: [post?.username],
      });

      resolvedProfession =
        profile.userProfession ||
        profile.profession ||
        post.userProfession ||
        "";

      resolvedPhotoURL =
        profile.photoURL ||
        profile.userPhotoURL ||
        currentUser.photoURL ||
        post.userPhotoURL ||
        "";
    } catch (e) {
      console.warn(
        "Failed to resolve user profile for post. Falling back to provided values.",
        e,
      );
      resolvedUsername = getDisplayName({
        authUser: currentUser || null,
        candidates: [resolvedUsername],
      });
      resolvedProfession = resolvedProfession || "";
      resolvedPhotoURL = resolvedPhotoURL || currentUser.photoURL || "";
    }

    const titleLower = (post.title || "").toLowerCase().trim();
    const titleWordsLower = tokenizeWords(post.title || "");
    const tagsLower = Array.isArray(post.tags)
      ? post.tags
          .filter((t) => t !== null && t !== undefined)
          .map((t) => String(t).toLowerCase().trim())
      : undefined;

    // For reshares, include original post's title/content in searchable fields
    // so reshares appear in search results for the original post's content
    const originalTitleLower = (post.type === 'shared_post' && post.sharedPostData?.title)
      ? post.sharedPostData.title.toLowerCase().trim()
      : '';
    const originalContentLower = (post.type === 'shared_post' && post.sharedPostData?.content)
      ? post.sharedPostData.content.toLowerCase().trim()
      : '';

    const postData = {
      username: resolvedUsername,
      userProfession: resolvedProfession || "",
      userPhotoURL: resolvedPhotoURL || "",
      userId: currentUser.uid,
      title: post.title,
      content: post.content,
      title_lower: titleLower,
      title_words_lower: titleWordsLower,
      actionTag: post.actionTag,
      issue: post.issue,
      scale: post.scale,
      location: post.location,
      status: "active",
      issueStatus: post.issueStatus || "",
      imageUrl: post.imageUrl || "",
      imageUrls: Array.isArray(post.imageUrls)
        ? post.imageUrls
        : post.imageUrl
          ? [post.imageUrl]
          : [],
      link: post.link || "",
      timestamp: serverTimestamp(),
      updatedAt: serverTimestamp(),
      likeCount: 0,
      commentCount: 0,
      shares: 0,
      sharers: [],
      clientRequestId: post.clientRequestId || null,
      type: post.type || "post",
      sharedPostId: post.sharedPostId || null,
      sharedPostData: post.sharedPostData || null,
      // Searchable fields for reshares: include original post title/content
      original_title_lower: originalTitleLower,
      original_content_lower: originalContentLower,
    };

    if (tagsLower) {
      postData.tags_lower = tagsLower;
    }

    const docRef = await addDoc(postsRef, postData);

    const createdDoc = await getDoc(docRef);
    const createdData = createdDoc.data();

    return {
      id: docRef.id,
      ...createdData,
      timestamp: createdData.timestamp.toDate(),
    };
  } catch (error) {
    console.error("Error saving post:", error);
    throw error;
  }
};

/**
 * Delete a post
 * @param {string} postId - Post ID
 * @param {string} userId - User ID
 * @returns {Promise<boolean>}
 */
export const deletePost = async (postId, userId) => {
  try {
    const postRef = doc(db, "posts", postId);
    const postDoc = await getDoc(postRef);

    if (!postDoc.exists()) {
      throw new Error("Post not found");
    }

    const postData = postDoc.data();
    const currentUid = auth?.currentUser?.uid || userId || null;
    if (!currentUid) {
      throw new Error("Not authenticated");
    }

    const ownerId = postData.userId || postData.uid || null;
    if (!ownerId || ownerId !== currentUid) {
      throw new Error("You are not authorized to delete this post");
    }

    await deleteDoc(postRef);

    try {
      const ownerRef = doc(db, "users", ownerId);
      await updateDoc(ownerRef, { postCount: increment(-1) });
    } catch (e) {
      const code = e?.code || e?.message || String(e);
      console.warn("[deletePost] Skipped updating user.postCount:", code);
    }

    return true;
  } catch (error) {
    console.error("Error deleting post:", error);
    throw error;
  }
};

/**
 * Backfill post status to active
 * @param {Object} options - Options including batchSize
 * @returns {Promise<number>} - Number of posts updated
 */
export const backfillPostStatusActive = async ({ batchSize = 200 } = {}) => {
  let last = null;
  let updated = 0;
  while (true) {
    const postsRef = collection(db, "posts");
    const q = last
      ? query(
          postsRef,
          orderBy("timestamp", "desc"),
          startAfter(last),
          limit(batchSize),
        )
      : query(postsRef, orderBy("timestamp", "desc"), limit(batchSize));
    const snap = await getDocs(q);
    if (snap.empty) break;
    for (const d of snap.docs) {
      const data = d.data();
      const rawStatus = data?.status;
      const normalized =
        typeof rawStatus === "string" ? rawStatus.trim().toLowerCase() : "";
      const needsStatus =
        !normalized || !["active", "deleted", "archived"].includes(normalized);
      const hasTimestamp = !!data?.timestamp;
      const fallbackTs =
        data?.createdAt || data?.updatedAt || serverTimestamp();

      const updates = {};
      if (needsStatus) updates.status = "active";
      if (!hasTimestamp) updates.timestamp = fallbackTs;

      if (Object.keys(updates).length > 0) {
        try {
          await updateDoc(d.ref, updates);
          updated++;
        } catch {}
      }
    }
    last = snap.docs[snap.docs.length - 1];
  }
  return updated;
};

/**
 * Update post fields
 * @param {string} postId - Post ID
 * @param {Object} updates - Fields to update
 * @returns {Promise<Object>} - Updated post
 */
export const updatePostFields = async (postId, updates = {}) => {
  try {
    if (!auth?.currentUser) throw new Error("No authenticated user");
    if (!postId) throw new Error("Missing postId");
    const postRef = doc(db, "posts", postId);
    const snap = await getDoc(postRef);
    if (!snap.exists()) throw new Error("Post not found");
    const data = snap.data();
    const ownerId = data?.userId || data?.uid || null;
    if (!ownerId || ownerId !== auth.currentUser.uid) {
      throw new Error("You are not authorized to edit this post");
    }
    const allowed = [
      "title",
      "content",
      "actionTag",
      "issue",
      "scale",
      "location",
      "issueStatus",
      "imageUrl",
      "imageUrls",
      "link",
    ];
    const payload = {};
    for (const key of allowed) {
      if (Object.prototype.hasOwnProperty.call(updates, key)) {
        payload[key] = updates[key];
      }
    }
    if (Object.prototype.hasOwnProperty.call(payload, "title")) {
      const t = String(payload.title || "");
      payload.title_lower = t.toLowerCase().trim();
      payload.title_words_lower = tokenizeWords(t);
    }
    payload.updatedAt = serverTimestamp();
    await updateDoc(postRef, payload);
    const updatedSnap = await getDoc(postRef);
    return { id: updatedSnap.id, ...updatedSnap.data() };
  } catch (e) {
    console.error("Error updating post fields:", e);
    throw e;
  }
};
